/**
 * Utility to convert hex color codes to human-readable color names
 * for accessibility purposes (screen reader announcements)
 */

/**
 * Convert a hex color to the nearest basic color name
 * @param {string} hex - Hex color code (e.g., "#b300e0" or "b300e0")
 * @returns {string} Human-readable color name
 */
export function hexToColorName(hex) {
    if( !hex || (typeof hex !== 'string') || (hex.length < 6) ) {
        return 'unknown color';
    }
    // Remove # if present and convert to lowercase
    hex = hex.replace('#', '').toLowerCase();

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Convert RGB to HSL for better color categorization
    const hsl = rgbToHsl(r, g, b);
    const h = hsl.h;
    const s = hsl.s;
    const l = hsl.l;

    // Handle grayscale colors
    if (s < 0.1) {
        if (l < 0.15) return 'black';
        if (l < 0.30) return 'dark gray';
        if (l < 0.50) return 'gray';
        if (l < 0.70) return 'light gray';
        if (l < 0.90) return 'very light gray';
        return 'white';
    }

    // Determine lightness modifier
    let lightnessModifier = '';
    if (l < 0.25) {
        lightnessModifier = 'very dark ';
    } else if (l < 0.40) {
        lightnessModifier = 'dark ';
    } else if (l > 0.75) {
        lightnessModifier = 'light ';
    } else if (l > 0.85) {
        lightnessModifier = 'very light ';
    }

    // Determine saturation modifier
    let saturationModifier = '';
    if (s < 0.25) {
        saturationModifier = 'grayish ';
    } else if (s < 0.50) {
        saturationModifier = 'muted ';
    }

    // Determine base color by hue
    let baseColor;
    if (h < 15 || h >= 345) {
        baseColor = 'red';
    } else if (h < 45) {
        baseColor = 'orange';
    } else if (h < 70) {
        baseColor = 'yellow';
    } else if (h < 150) {
        baseColor = 'green';
    } else if (h < 200) {
        baseColor = 'cyan';
    } else if (h < 260) {
        baseColor = 'blue';
    } else if (h < 290) {
        baseColor = 'purple';
    } else if (h < 320) {
        baseColor = 'magenta';
    } else {
        baseColor = 'pink';
    }

    return `${lightnessModifier}${saturationModifier}${baseColor}`.trim();
}

/**
 * Convert RGB to HSL
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {{h: number, s: number, l: number}} HSL values (h: 0-360, s: 0-1, l: 0-1)
 */
function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }

    return {
        h: h * 360,
        s: s,
        l: l
    };
}

